import { FC, useMemo } from 'react'

import {
  Text,
  InputSelectControlField,
  MultiSelectControl,
  Primary13Regular,
  RedRed60,
  Flex
} from '@sequencehq/core-components'
import { useLoadAllItemsQuery } from '@sequencehq/api'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import { ValidationResult } from 'Automations/nodeDefinitions/validation'

type BaseProps = {
  'data-testid'?: string
  validationErrors?: ValidationResult[]
  /** When true, the picker auto-opens on mount. */
  initiallyOpen?: boolean
}

type SingleSelectProps = BaseProps & {
  multiple?: false
  value: string
  onChange: (value: string) => void
}

type MultiSelectProps = BaseProps & {
  multiple: true
  value: string[]
  onChange: (value: string[]) => void
}

export type ProductSelectorProps = SingleSelectProps | MultiSelectProps

/**
 * Product picker shared by `ProductValueInput` and `ProductDiscountValueInput`.
 * Set `multiple` to switch between a single-select dropdown (string value)
 * and a multi-select popover (string[] value). The product list, sorting,
 * loading state, and validation rendering are shared across both modes.
 */
export const ProductSelector: FC<ProductSelectorProps> = props => {
  const { 'data-testid': dataTestId, validationErrors, initiallyOpen } = props
  const { data, hasNextPage, isLoading } = useLoadAllItemsQuery(
    dashboardv20240509Client.getProducts,
    { limit: 100 }
  )

  const options = useMemo(() => {
    const products = data?.pages?.flatMap(page => page.items) ?? []
    return products
      .map(product => ({ label: product.name, value: product.id }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
      )
  }, [data])

  const firstError = validationErrors?.at(0)

  return (
    <Flex gap="8px" flexDirection="column">
      {props.multiple ? (
        <MultiSelectControl
          data-testid={dataTestId}
          entityName="product"
          options={options}
          values={
            // Legacy configs may hold a bare string rather than a list.
            Array.isArray(props.value) ? props.value : []
          }
          onChange={props.onChange}
          isInvalid={!!firstError}
          disabled={isLoading || hasNextPage}
          initiallyOpen={initiallyOpen}
        />
      ) : (
        <InputSelectControlField
          data-testid={dataTestId}
          matchWidth={false}
          value={props.value ?? ''}
          onChange={selected => props.onChange(String(selected))}
          options={options}
          placeholder="Select product..."
          validationErrors={
            validationErrors?.map(e => ({ message: e.message })) ?? []
          }
          menuWidth={{ max: '320px', min: '100px' }}
          initiallyOpen={initiallyOpen}
        />
      )}

      {props.multiple && firstError && (
        <Text {...Primary13Regular} color={RedRed60}>
          {firstError.message}
        </Text>
      )}
    </Flex>
  )
}
